/**
 * DataLoader — Central data loading, validation, and access system.
 * All game data (races, attributes, skill trees, classes, feats, items, enemies,
 * regions, status effects) is loaded from JSON files and made accessible through
 * a unified API.
 *
 * Supports runtime registration of new entries for extensibility.
 *
 * @module DataLoader
 */

import eventBus from '../core/EventBus.js';

// JSON imports (Vite handles these as modules)
import racesData from '../data/races.json';
import attributesData from '../data/attributes.json';
import skillTreesData from '../data/skill_trees.json';
import classesData from '../data/classes.json';
import featsData from '../data/feats.json';
import itemsData from '../data/items.json';
import enemiesData from '../data/enemies.json';
import regionsData from '../data/regions.json';
import statusEffectsData from '../data/status_effects.json';
import companionsData from '../data/companions.json';
import packAnimalsData from '../data/pack_animals.json';
import vehiclesData from '../data/vehicles.json';

/**
 * Schema definitions for validation.
 * Each key maps a data type to its required fields and their expected types.
 */
const SCHEMAS = {
  races: {
    required: ['id', 'name', 'description', 'baseAttributes', 'attributeBonuses', 'traits', 'size', 'playable'],
    types: {
      id: 'string',
      name: 'string',
      description: 'string',
      lore: 'string',
      baseAttributes: 'object',
      attributeBonuses: 'object',
      traits: 'array',
      size: 'string',
      playable: 'boolean',
      portraitTags: 'array'
    }
  },
  attributes: {
    required: ['id', 'name', 'abbreviation', 'description', 'effects', 'combatScaling'],
    types: {
      id: 'string',
      name: 'string',
      abbreviation: 'string',
      description: 'string',
      effects: 'array',
      combatScaling: 'string'
    }
  },
  skill_trees: {
    required: ['id', 'name', 'description', 'soloClassName', 'skills'],
    types: {
      id: 'string',
      name: 'string',
      description: 'string',
      soloClassName: 'string',
      skills: 'array'
    }
  },
  skills: {
    required: ['id', 'name', 'tier', 'maxLevel', 'apCost', 'manaCost', 'cooldown', 'targetType', 'isPassive'],
    types: {
      id: 'string',
      name: 'string',
      tier: 'number',
      maxLevel: 'number',
      apCost: 'number',
      manaCost: 'array',
      cooldown: 'number',
      targetType: 'string',
      isPassive: 'boolean',
      description: 'string'
    }
  },
  feats: {
    required: ['id', 'name', 'description', 'levelRequired', 'effects', 'requirements'],
    types: {
      id: 'string',
      name: 'string',
      description: 'string',
      levelRequired: 'number',
      effects: 'array',
      requirements: 'object'
    }
  },
  enemies: {
    required: ['id', 'name', 'description', 'levelRange', 'regions', 'baseStats', 'abilities', 'behavior', 'loot', 'disposition', 'size'],
    types: {
      id: 'string',
      name: 'string',
      description: 'string',
      levelRange: 'array',
      regions: 'array',
      baseStats: 'object',
      abilities: 'array',
      behavior: 'string',
      loot: 'object',
      disposition: 'string',
      size: 'string'
    }
  },
  regions: {
    required: ['id', 'name', 'description', 'biome', 'levelRange', 'connections', 'enemies', 'events'],
    types: {
      id: 'string',
      name: 'string',
      description: 'string',
      biome: 'string',
      levelRange: 'array',
      connections: 'array',
      enemies: 'array',
      events: 'array'
    }
  },
  status_effects: {
    required: ['id', 'name', 'description', 'icon', 'duration', 'stackable', 'effects'],
    types: {
      id: 'string',
      name: 'string',
      description: 'string',
      icon: 'string',
      duration: 'number',
      stackable: 'boolean',
      effects: 'array'
    }
  },
  item_templates: {
    required: ['id', 'name', 'slot', 'category', 'baseStats', 'levelRange', 'rarity'],
    types: {
      id: 'string',
      name: 'string',
      slot: 'string',
      category: 'string',
      baseStats: 'object',
      levelRange: 'array',
      rarity: 'string'
    }
  },
  item_affixes: {
    required: ['id', 'name', 'effect', 'rarityMin', 'slots'],
    types: {
      id: 'string',
      name: 'string',
      effect: 'object',
      rarityMin: 'string',
      slots: 'array'
    }
  },
  item_uniques: {
    required: ['id', 'name', 'slot', 'category', 'baseStats', 'level', 'rarity', 'fixedAffixes'],
    types: {
      id: 'string',
      name: 'string',
      slot: 'string',
      category: 'string',
      baseStats: 'object',
      level: 'number',
      rarity: 'string',
      fixedAffixes: 'array'
    }
  },
  companions: {
    required: ['id', 'name', 'type', 'sourceId', 'behavior', 'stats', 'scalingType'],
    types: {
      id: 'string',
      name: 'string',
      type: 'string',
      sourceId: 'string',
      behavior: 'string',
      stats: 'object',
      scalingType: 'string',
      description: 'string'
    }
  },
  pack_animals: {
    required: ['id', 'name', 'description', 'carryCapacity', 'speedModifier', 'cost'],
    types: {
      id: 'string',
      name: 'string',
      description: 'string',
      carryCapacity: 'number',
      speedModifier: 'number',
      cost: 'number'
    }
  },
  vehicles: {
    required: ['id', 'name', 'description', 'capacity', 'speedModifier', 'cost'],
    types: {
      id: 'string',
      name: 'string',
      description: 'string',
      capacity: 'number',
      speedModifier: 'number',
      cost: 'number'
    }
  }
};

/** Valid enum values for validation */
const VALID_VALUES = {
  size: ['small', 'medium', 'large', 'huge'],
  targetType: ['single_enemy', 'single_ally', 'self', 'aoe_enemy', 'aoe_ally', 'line', 'adjacent', 'tile'],
  damageType: ['physical', 'fire', 'frost', 'lightning', 'arcane', 'shadow', 'holy', 'poison', 'nature', 'healing', null],
  behavior: ['melee', 'ranged', 'support', 'tank', 'boss'],
  disposition: ['passive', 'neutral', 'aggressive'],
  rarity: ['normal', 'magic', 'rare', 'unique', 'legendary'],
  slot: ['weapon', 'head', 'chest', 'legs', 'boots', 'gloves', 'offhand', 'ring', 'amulet']
};

class DataStore {
  constructor() {
    /** @type {Map<string, Map<string, object>>} */
    this._store = new Map();
    /** @type {Map<string, Function[]>} */
    this._hooks = new Map();
    /** @type {string[]} */
    this._validationErrors = [];
    /** @type {boolean} */
    this._loaded = false;

    // Classes data is structured differently (matrix + solo), store as-is
    this._classes = null;
    // Items data is structured differently (templates + affixes + uniques)
    this._items = null;
  }

  /**
   * Load all game data from JSON imports.
   * Validates each entry against its schema.
   * @returns {{ success: boolean, errors: string[] }}
   */
  loadAll() {
    this._validationErrors = [];

    // Initialize stores for array-based data
    this._initStore('races', racesData);
    this._initStore('attributes', attributesData);
    this._initStore('skill_trees', skillTreesData);
    this._initStore('feats', featsData);
    this._initStore('enemies', enemiesData);
    this._initStore('regions', regionsData);
    this._initStore('status_effects', statusEffectsData);
    this._initStore('companions', companionsData);
    this._initStore('pack_animals', packAnimalsData);
    this._initStore('vehicles', vehiclesData);

    // Build a flat skill index from skill trees
    this._buildSkillIndex();

    // Classes: special structure
    this._classes = structuredClone(classesData);

    // Items: special structure
    this._items = structuredClone(itemsData);
    this._initStore('item_templates', this._items.templates);
    this._initStore('item_uniques', this._items.uniques);
    // Index affixes by id
    const allAffixes = [
      ...(this._items.affixes?.prefixes || []),
      ...(this._items.affixes?.suffixes || [])
    ];
    this._initStore('item_affixes', allAffixes);

    // Validate all array-based data
    this._validateStore('races');
    this._validateStore('attributes');
    this._validateStore('skill_trees');
    this._validateStore('feats');
    this._validateStore('enemies');
    this._validateStore('regions');
    this._validateStore('status_effects');
    this._validateStore('item_templates');
    this._validateStore('item_uniques');
    this._validateStore('item_affixes');
    this._validateStore('companions');
    this._validateStore('pack_animals');
    this._validateStore('vehicles');

    // Validate skills within trees
    this._validateSkills();

    this._loaded = true;

    const result = {
      success: this._validationErrors.length === 0,
      errors: [...this._validationErrors]
    };

    if (result.errors.length > 0) {
      console.warn(`[DataStore] Loaded with ${result.errors.length} validation warning(s):`);
      result.errors.forEach(e => console.warn(`  - ${e}`));
    } else {
      console.log('[DataStore] All data loaded and validated successfully.');
    }

    eventBus.emit('data:loaded', {
      types: this._getLoadedTypes(),
      errors: result.errors
    });

    return result;
  }

  /**
   * Initialize a store section from an array of entries.
   * @param {string} type
   * @param {object[]} dataArray
   */
  _initStore(type, dataArray) {
    if (!Array.isArray(dataArray)) {
      this._validationErrors.push(`${type}: expected array, got ${typeof dataArray}`);
      this._store.set(type, new Map());
      return;
    }

    const map = new Map();
    for (const entry of dataArray) {
      if (!entry.id) {
        this._validationErrors.push(`${type}: entry missing 'id' field: ${JSON.stringify(entry).slice(0, 80)}`);
        continue;
      }
      if (map.has(entry.id)) {
        this._validationErrors.push(`${type}: duplicate id '${entry.id}'`);
      }
      map.set(entry.id, structuredClone(entry));
    }
    this._store.set(type, map);
  }

  /**
   * Build a flat index of all skills from all skill trees.
   */
  _buildSkillIndex() {
    const skillMap = new Map();
    const treesMap = this._store.get('skill_trees');
    if (!treesMap) return;

    for (const [, tree] of treesMap) {
      if (!Array.isArray(tree.skills)) continue;
      for (const skill of tree.skills) {
        if (skill.id) {
          skillMap.set(skill.id, { ...skill, treeId: tree.id });
        }
      }
    }
    this._store.set('skills', skillMap);
  }

  /**
   * Validate all entries in a store section against the schema.
   * @param {string} type
   */
  _validateStore(type) {
    const schema = SCHEMAS[type];
    if (!schema) return;

    const map = this._store.get(type);
    if (!map) return;

    for (const [id, entry] of map) {
      this._validateEntry(type, id, entry, schema);
    }
  }

  /**
   * Validate a single entry against a schema.
   * @param {string} type
   * @param {string} id
   * @param {object} entry
   * @param {object} schema
   */
  _validateEntry(type, id, entry, schema) {
    // Check required fields
    for (const field of schema.required) {
      if (entry[field] === undefined || entry[field] === null) {
        this._validationErrors.push(`${type}/${id}: missing required field '${field}'`);
      }
    }

    // Check types
    for (const [field, expectedType] of Object.entries(schema.types)) {
      if (entry[field] === undefined || entry[field] === null) continue;

      const val = entry[field];
      if (expectedType === 'array' && !Array.isArray(val)) {
        this._validationErrors.push(`${type}/${id}: field '${field}' expected array, got ${typeof val}`);
      } else if (expectedType === 'object' && (typeof val !== 'object' || Array.isArray(val))) {
        this._validationErrors.push(`${type}/${id}: field '${field}' expected object, got ${Array.isArray(val) ? 'array' : typeof val}`);
      } else if (expectedType !== 'array' && expectedType !== 'object' && typeof val !== expectedType) {
        this._validationErrors.push(`${type}/${id}: field '${field}' expected ${expectedType}, got ${typeof val}`);
      }
    }
  }

  /**
   * Validate skill entries within all skill trees.
   */
  _validateSkills() {
    const skillSchema = SCHEMAS.skills;
    const treesMap = this._store.get('skill_trees');
    if (!treesMap || !skillSchema) return;

    for (const [treeId, tree] of treesMap) {
      if (!Array.isArray(tree.skills)) continue;
      for (const skill of tree.skills) {
        this._validateEntry('skills', `${treeId}/${skill.id}`, skill, skillSchema);

        // Validate enum values
        if (skill.targetType && !VALID_VALUES.targetType.includes(skill.targetType)) {
          this._validationErrors.push(`skills/${treeId}/${skill.id}: invalid targetType '${skill.targetType}'`);
        }
        if (skill.damageType !== undefined && !VALID_VALUES.damageType.includes(skill.damageType)) {
          this._validationErrors.push(`skills/${treeId}/${skill.id}: invalid damageType '${skill.damageType}'`);
        }

        // Validate array lengths match maxLevel
        if (skill.manaCost && skill.maxLevel && skill.manaCost.length !== skill.maxLevel) {
          this._validationErrors.push(`skills/${treeId}/${skill.id}: manaCost length (${skill.manaCost.length}) != maxLevel (${skill.maxLevel})`);
        }
        if (skill.damageMultiplier && skill.maxLevel && Array.isArray(skill.damageMultiplier) && skill.damageMultiplier.length !== skill.maxLevel) {
          this._validationErrors.push(`skills/${treeId}/${skill.id}: damageMultiplier length (${skill.damageMultiplier.length}) != maxLevel (${skill.maxLevel})`);
        }
      }
    }
  }

  /**
   * Validate a data entry against its type schema.
   * @param {string} type - The data type (e.g., 'races', 'feats')
   * @param {object} data - The data entry to validate
   * @returns {{ valid: boolean, errors: string[] }}
   */
  validate(type, data) {
    const schema = SCHEMAS[type];
    if (!schema) {
      return { valid: false, errors: [`Unknown data type: ${type}`] };
    }

    const errors = [];
    for (const field of schema.required) {
      if (data[field] === undefined || data[field] === null) {
        errors.push(`Missing required field '${field}'`);
      }
    }

    for (const [field, expectedType] of Object.entries(schema.types)) {
      if (data[field] === undefined || data[field] === null) continue;
      const val = data[field];
      if (expectedType === 'array' && !Array.isArray(val)) {
        errors.push(`Field '${field}' expected array, got ${typeof val}`);
      } else if (expectedType === 'object' && (typeof val !== 'object' || Array.isArray(val))) {
        errors.push(`Field '${field}' expected object`);
      } else if (expectedType !== 'array' && expectedType !== 'object' && typeof val !== expectedType) {
        errors.push(`Field '${field}' expected ${expectedType}, got ${typeof val}`);
      }
    }

    return { valid: errors.length === 0, errors };
  }

  /**
   * Get a single entry by type and ID.
   * @param {string} type - Data type (e.g., 'races', 'enemies', 'skills')
   * @param {string} id - Entry ID
   * @returns {object|null}
   */
  getById(type, id) {
    // Special cases
    if (type === 'classes') {
      return this._getClassByKey(id);
    }

    const map = this._store.get(type);
    if (!map) return null;

    const entry = map.get(id);
    if (!entry) return null;

    // Run hooks
    this._runHooks(type, 'access', entry);

    return structuredClone(entry);
  }

  /**
   * Get all entries of a given type.
   * @param {string} type - Data type
   * @returns {object[]}
   */
  getAll(type) {
    // Special cases
    if (type === 'classes') {
      return this._classes ? structuredClone(this._classes) : null;
    }
    if (type === 'items') {
      return this._items ? structuredClone(this._items) : null;
    }

    const map = this._store.get(type);
    if (!map) return [];

    const results = [];
    for (const [, entry] of map) {
      results.push(structuredClone(entry));
    }
    return results;
  }

  /**
   * Register a new data entry at runtime.
   * Validates against the schema before adding.
   * @param {string} type - Data type
   * @param {object} data - The entry to register
   * @returns {{ success: boolean, errors: string[] }}
   */
  register(type, data) {
    if (!data || !data.id) {
      return { success: false, errors: ['Entry must have an id field'] };
    }

    // Validate if schema exists
    const validation = this.validate(type, data);
    if (!validation.valid) {
      return { success: false, errors: validation.errors };
    }

    // Get or create the store map
    if (!this._store.has(type)) {
      this._store.set(type, new Map());
    }

    const map = this._store.get(type);
    const isNew = !map.has(data.id);
    map.set(data.id, structuredClone(data));

    // Run hooks
    this._runHooks(type, isNew ? 'register' : 'update', data);

    eventBus.emit(`data:${isNew ? 'registered' : 'updated'}`, { type, id: data.id });

    return { success: true, errors: [] };
  }

  /**
   * Register a hook function that is called on data access or modification.
   * @param {string} type - Data type to hook into
   * @param {Function} hookFn - Function(action, data) called on access/register/update
   */
  registerHook(type, hookFn) {
    if (!this._hooks.has(type)) {
      this._hooks.set(type, []);
    }
    this._hooks.get(type).push(hookFn);
  }

  /**
   * Remove a previously registered hook.
   * @param {string} type
   * @param {Function} hookFn
   */
  removeHook(type, hookFn) {
    const hooks = this._hooks.get(type);
    if (!hooks) return;
    const idx = hooks.indexOf(hookFn);
    if (idx !== -1) hooks.splice(idx, 1);
  }

  /**
   * Run all hooks for a given type and action.
   * @param {string} type
   * @param {string} action
   * @param {object} data
   * @private
   */
  _runHooks(type, action, data) {
    const hooks = this._hooks.get(type);
    if (!hooks) return;
    for (const fn of hooks) {
      try {
        fn(action, data);
      } catch (err) {
        console.error(`[DataStore] Hook error for ${type}/${action}:`, err);
      }
    }
  }

  /**
   * Look up a class name from the class matrix.
   * @param {string} key - Either a solo tree ID or "tree1+tree2" combo key
   * @returns {string|null}
   */
  _getClassByKey(key) {
    if (!this._classes) return null;

    // Check solo classes first
    if (this._classes.soloClasses[key]) {
      return this._classes.soloClasses[key];
    }

    // Check matrix (try both orderings)
    if (this._classes.matrix[key]) {
      return this._classes.matrix[key];
    }

    // Try reversed order
    const parts = key.split('+');
    if (parts.length === 2) {
      const reversed = `${parts[1]}+${parts[0]}`;
      if (this._classes.matrix[reversed]) {
        return this._classes.matrix[reversed];
      }
    }

    return null;
  }

  /**
   * Get a list of item templates filtered by slot and/or level.
   * @param {{ slot?: string, level?: number, category?: string }} filters
   * @returns {object[]}
   */
  getItemTemplates(filters = {}) {
    const templates = this.getAll('item_templates');
    return templates.filter(t => {
      if (filters.slot && t.slot !== filters.slot) return false;
      if (filters.category && t.category !== filters.category) return false;
      if (filters.level !== undefined) {
        if (t.levelRange && (filters.level < t.levelRange[0] || filters.level > t.levelRange[1])) return false;
      }
      return true;
    });
  }

  /**
   * Get applicable affixes for a given slot and rarity.
   * @param {{ slot?: string, rarity?: string, type?: 'prefix'|'suffix' }} filters
   * @returns {object[]}
   */
  getAffixes(filters = {}) {
    if (!this._items) return [];

    const rarityOrder = ['normal', 'magic', 'rare', 'unique', 'legendary'];
    const results = [];

    const checkPool = (pool, affixType) => {
      if (filters.type && filters.type !== affixType) return;
      for (const affix of pool) {
        if (filters.slot && !affix.slots.includes(filters.slot)) continue;
        if (filters.rarity) {
          const minIdx = rarityOrder.indexOf(affix.rarityMin);
          const reqIdx = rarityOrder.indexOf(filters.rarity);
          if (reqIdx < minIdx) continue;
        }
        results.push({ ...affix, affixType });
      }
    };

    checkPool(this._items.affixes.prefixes, 'prefix');
    checkPool(this._items.affixes.suffixes, 'suffix');

    return results;
  }

  /**
   * Get enemies that can appear in a given region.
   * @param {string} regionId
   * @returns {object[]}
   */
  getEnemiesByRegion(regionId) {
    const region = this.getById('regions', regionId);
    if (!region) return [];

    return region.enemies
      .map(enemyId => this.getById('enemies', enemyId))
      .filter(Boolean);
  }

  /**
   * Get the class name for a pair of skill trees.
   * @param {string} tree1 - First tree ID
   * @param {string|null} tree2 - Second tree ID (null for solo class)
   * @returns {string}
   */
  getClassName(tree1, tree2 = null) {
    if (!tree2) {
      return this._getClassByKey(tree1) || 'Adventurer';
    }

    // Try both orderings
    const result = this._getClassByKey(`${tree1}+${tree2}`);
    if (result) return result;

    // Fallback to solo class of first tree
    const soloClass = this._getClassByKey(tree1);
    return soloClass || 'Adventurer';
  }

  /**
   * Get all skills belonging to a specific skill tree.
   * @param {string} treeId
   * @returns {object[]}
   */
  getSkillsByTree(treeId) {
    const tree = this.getById('skill_trees', treeId);
    if (!tree) return [];
    return tree.skills || [];
  }

  /**
   * Get feats available at a specific level.
   * @param {number} level
   * @returns {object[]}
   */
  getFeatsForLevel(level) {
    return this.getAll('feats').filter(f => f.levelRequired <= level);
  }

  /**
   * Check if the data store has been loaded.
   * @returns {boolean}
   */
  get isLoaded() {
    return this._loaded;
  }

  /**
   * Get summary of all loaded data types and their counts.
   * @returns {object}
   */
  getSummary() {
    const summary = {};
    for (const [type, map] of this._store) {
      summary[type] = map.size;
    }
    if (this._classes) {
      summary.classes_matrix = Object.keys(this._classes.matrix).length;
      summary.classes_solo = Object.keys(this._classes.soloClasses).length;
    }
    return summary;
  }

  /**
   * @returns {string[]}
   * @private
   */
  _getLoadedTypes() {
    return [...this._store.keys()];
  }
}

// Export as singleton
const dataStore = new DataStore();
export default dataStore;
export { DataStore };
